import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Card, CardContent } from './ui/card';
import { Input } from './ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Button } from './ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import { Edit, Trash2, Download, UserPlus, ChevronRight, Plus, X, Pencil, Trash, ArrowLeft, MoreVertical } from 'lucide-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { cn, getLocalDateString, GST_MAX_LENGTH, sanitizeGstInput } from '../lib/utils';
import { useIsAndroid } from '../hooks/use-android';
import { Dialog, DialogTrigger, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose, DialogDescription } from './ui/dialog';
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from './ui/form';
import { Label } from './ui/label';
import { toast } from './ui/use-toast';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import * as z from 'zod';
import { Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { FaWhatsapp } from 'react-icons/fa';
import { MdMessage } from 'react-icons/md';
import { getBaseUrl } from '@/lib/api';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { saveAs } from 'file-saver';
import WhatsAppPopup from './WhatsAppPopup';
import { Dialog as UIDialog, DialogContent as UIDialogContent, DialogHeader as UIDialogHeader, DialogTitle as UIDialogTitle, DialogClose as UIDialogClose } from './ui/dialog';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from './ui/dropdown-menu';
import { Popover, PopoverTrigger, PopoverContent } from './ui/popover';
import { Capacitor } from '@capacitor/core';
import { downloadPdf } from '@/lib/pdfDownload';
import { downloadExcel } from '@/lib/excelDownload';
import { AppBar } from './ui/app-bar';
import { useIsMobile } from '../hooks/use-mobile';
import { useBackNavigation } from '../hooks/use-back-navigation';
import { useCompanyState } from '../hooks/useCompanyState';
import { useUserPrivileges } from '@/context/UserPrivilegesContext';
import { PRIVILEGE } from '@/lib/privilegeConstants';


interface Customer {
  Customercode: number;
  FName: string;
  LName: string | null;
  Phone: string;
  Emailid: string | null;
  GSTN: string | null;
  Address: string | null;
  locality: string | null;
  State: string | null;
  CGroupID?: number;
  OpeningBalance?: number;
  CGroupName?: string | null;
}

interface Transaction {
  Date: string;
  "Tx Type": string;
  "Invoice Amt": number;
  Payment: number;
  "Due Amount": number;
  SortOrder: number;
}

interface TransactionResponse {
  status: number;
  message: string;
  data: Transaction[];
  TotalRecords: number;
}

interface CustomerPageProps {
  token: string;
  onBack?: () => void;
}

// Column definitions for better maintainability
const COLUMNS = {
  name: { key: 'FName', label: 'Name' },
  company: { key: 'LName', label: 'Company' },
  phone: { key: 'Phone', label: 'Mobile' },
  email: { key: 'Emailid', label: 'Email' },
  gstn: { key: 'GSTN', label: 'GSTN' },
  location: { key: 'locality', label: 'Location' },
  address: { key: 'Address', label: 'Address' },
  state: { key: 'State', label: 'State' },
};

const formatDate = (dateString: string | null): string => {
  if (!dateString) return 'N/A';
  try {
    return format(new Date(dateString), 'dd-MM-yyyy');
  } catch (error) {
    return 'Invalid Date';
  }
};

// Helper function to truncate text to 12 characters (for transactions only)
const truncateText = (text: string, maxLength: number = 12): string => {
  if (!text) return '';
  return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
};

const CustomerCard = ({ customer, expandedRow, handleRowClick, handleEdit, handleDelete, transactionData, formatDate, canUpdate, canDelete }) => {
  return (
    <div className="relative">
      <div className="bg-gradient-to-r from-purple-600 to-purple-800 rounded-lg p-4 text-white">
        <div className="flex items-start justify-between mb-2">
          <div className="flex items-start gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleRowClick(customer.Customercode)}
              className="h-8 w-8 rounded-full text-white hover:bg-white/20 transition-all duration-200 -ml-2 flex-shrink-0"
            >
              <ChevronRight 
                className={cn(
                  "h-5 w-5 transition-transform duration-200 ease-in-out",
                  expandedRow === customer.Customercode ? "transform rotate-90" : ""
                )}
              />
            </Button>
            <h3 className="text-lg font-semibold break-words leading-tight">
              {customer.FName} {customer.LName}
            </h3>
          </div>
          <div className="flex items-center gap-2">
            {(canUpdate || canDelete) && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="h-8 w-8 text-gray-600 hover:bg-gray-100">
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {canUpdate && (
                <DropdownMenuItem onClick={() => handleEdit(customer)} className="text-blue-600">
                  <Edit className="h-4 w-4 mr-2" />
                  Update
                </DropdownMenuItem>
                )}
                {canDelete && (
                <DropdownMenuItem onClick={() => handleDelete(customer)} className="text-red-600">
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete
                </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
            )}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-y-2">
          <div>
            <div className="text-white/70">Phone:</div>
            <div>{customer.Phone}</div>
          </div>
          <div>
            <div className="text-white/70">First Sale:</div>
            <div>{formatDate(customer["First sale date"])}</div>
          </div>
          <div>
            <div className="text-white/70">Last Sale:</div>
            <div>{formatDate(customer["Last sale date"])}</div>
          </div>
          <div>
            <div className="text-white/70">Total Due:</div>
            <div>₹{customer["Total Due Amount"].toFixed(2)}</div>
          </div>
          <div>
            <div className="text-white/70">Total Invoices:</div>
            <div>{customer["Total Invoices Made"]}</div>
          </div>
          <div>
            <div className="text-white/70">Avg Value:</div>
            <div>₹{customer["Avg Invoice Value"].toFixed(2)}</div>
          </div>
        </div>
      </div>
      {expandedRow === customer.Customercode && (
        <div className="mt-2 bg-white rounded-lg shadow-sm p-4">
          <div className="text-lg font-semibold mb-4 text-gray-900">
            Recent Transactions
          </div>
          {!transactionData?.data ? (
            <div className="text-center py-3 text-gray-500">Loading transactions...</div>
          ) : transactionData.data.length === 0 || !((transactionData.data || []).some((tx: any) => ((tx["Invoice Amt"] ?? 0) !== 0 || (tx.Payment ?? 0) !== 0 || (tx["Due Amount"] ?? 0) !== 0))) ? (
            <div className="text-center py-3 text-gray-500">No transactions found</div>
          ) : (
            <div className="space-y-3">
              {transactionData.data.map((transaction, index) => (
                <div key={index} className="border-b border-gray-200 pb-3 last:border-0">
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <div className="text-gray-500">Date:</div>
                      <div>{formatDate(transaction.Date)}</div>
                    </div>
                    <div>
                      <div className="text-gray-500">Type:</div>
                      <div>{transaction["Tx Type"]}</div>
                    </div>
                    <div>
                      <div className="text-gray-500">Invoice Amount:</div>
                      <div>₹{transaction["Invoice Amt"].toFixed(2)}</div>
                    </div>
                    <div>
                      <div className="text-gray-500">Payment:</div>
                      <div>₹{transaction.Payment.toFixed(2)}</div>
                    </div>
                    <div className="col-span-2">
                      <div className="text-gray-500">Due Amount:</div>
                      <div>₹{transaction["Due Amount"].toFixed(2)}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Add Customer form schema
const customerSchema = z.object({
  MobileNo: z.string()
    .regex(/^[0-9]{10}$/, "Mobile number must be exactly 10 digits and contain only numbers."),
  ContactName: z.string().optional(),
  CompanyName: z.string().optional(),
  EmailID: z.string().optional(),
  GSTNo: z.string().optional().refine(
    val => !val || (val.length <= 15 && /^[A-Za-z0-9]*$/.test(val)),
    { message: 'GST number must be alphanumeric and should not exceed 15 characters.' }
  ),
  Shipping_Address: z.string().optional(),
  Locality: z.string().optional(),
  State: z.string().optional(),
  CGroupID: z.string()
    .min(1, { message: 'Group name is required' })
    .refine(val => !isNaN(Number(val)), { message: 'Group name is required' }),
  OpeningBalance: z.coerce.number().optional(),
});

type CustomerFormValues = z.infer<typeof customerSchema>;

// Group type
interface Group {
  id: number;
  Name: string;
}

// Update form schema for update modal
const updateCustomerSchema = z.object({
  Customercode: z.number(),
  MobileNo: z.string()
    .regex(/^[0-9]{10}$/, "Mobile number must be exactly 10 digits and contain only numbers."),
  ContactName: z.string().optional(),
  CompanyName: z.string().optional(),
  EmailID: z.string().optional(),
  GSTNo: z.string().optional().refine(
    val => !val || (val.length <= 15 && /^[A-Za-z0-9]*$/.test(val)),
    { message: 'GST number must be alphanumeric and should not exceed 15 characters.' }
  ),
  Shipping_Address: z.string().optional(),
  Locality: z.string().optional(),
  State: z.string().optional(),
  CGroupID: z.string().optional(), // keep as string for form, convert to int in submit
});
type UpdateCustomerFormValues = z.infer<typeof updateCustomerSchema>;

const CustomerPage = ({ token, onBack }: CustomerPageProps) => {
  console.log('onBack prop in CustomerPage:', onBack); // Debugging line
  const [searchQuery, setSearchQuery] = useState('');
  const [pageSize, setPageSize] = useState('50');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedGroupId, setSelectedGroupId] = useState<number>(0);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState<Customer | null>(null);
  const [customerToDelete, setCustomerToDelete] = useState<Customer | null>(null);
  const [groups, setGroups] = useState<Group[]>([]);
  const [editingGroup, setEditingGroup] = useState<Group | null>(null);
  const [newGroupName, setNewGroupName] = useState('');
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [mobileLoadedCount, setMobileLoadedCount] = useState(20);
  const [isMobileLoading, setIsMobileLoading] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const mobileCardsContainerRef = useRef<HTMLDivElement>(null);
  const [showWhatsAppModal, setShowWhatsAppModal] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [exportType, setExportType] = useState<'customer' | 'transactions' | null>(null);
  const [showScrollToTop, setShowScrollToTop] = useState(false);
  const [allCustomers, setAllCustomers] = useState<Customer[]>([]);
  const [mobileCurrentPage, setMobileCurrentPage] = useState(1);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMoreData, setHasMoreData] = useState(true);
  const isMobile = useIsMobile();
  const isAndroid = useIsAndroid();
  
  // Ledger state
  const [selectedCustomerCodes, setSelectedCustomerCodes] = useState<Set<number>>(new Set());
  const today = getLocalDateString();
  const [ledgerStartDate, setLedgerStartDate] = useState(today);
  const [ledgerEndDate, setLedgerEndDate] = useState(today);
  const [isGeneratingLedger, setIsGeneratingLedger] = useState(false);

  const [redeemPointsByCustomer, setRedeemPointsByCustomer] = useState<Record<number, number>>({});

  const baseUrl = getBaseUrl();
  const authToken = token.startsWith('Bearer ') ? token : `Bearer ${token}`;
  const { companyState } = useCompanyState();
  const { hasPrivilege } = useUserPrivileges();
  const canViewCustomer = hasPrivilege(PRIVILEGE.VIEW_CUSTOMER);
  const canCreateCustomer = hasPrivilege(PRIVILEGE.CUSTOMER_CREATION);
  const canUpdateCustomer = hasPrivilege(PRIVILEGE.UPDATE_CUSTOMER);
  const canDeleteCustomer = hasPrivilege(PRIVILEGE.DELETE_CUSTOMER);

  const form = useForm<CustomerFormValues>({
    resolver: zodResolver(customerSchema),
    defaultValues: {
      MobileNo: '',
      ContactName: '',
      CompanyName: '',
      EmailID: '',
      GSTNo: '',
      Shipping_Address: '',
      Locality: '',
      State: '',
      CGroupID: '',
      OpeningBalance: 0,
    },
  });

  const updateForm = useForm<UpdateCustomerFormValues>({
    resolver: zodResolver(updateCustomerSchema),
    defaultValues: {
      Customercode: 0,
      MobileNo: '',
      ContactName: '',
      CompanyName: '',
      EmailID: '',
      GSTNo: '',
      Shipping_Address: '',
      Locality: '',
      State: '',
      CGroupID: '',
    },
  });

  const { data: customersData, isLoading } = useQuery({
    queryKey: ['customers', searchQuery, pageSize, currentPage, selectedGroupId],
    enabled: canViewCustomer,
    queryFn: async () => {
      const isAll = pageSize === 'all';
      const limit = isAll ? 1 : parseInt(pageSize);
      const offset = isAll ? 0 : (currentPage - 1) * limit;
      const filterParam = isAll ? '&filter=all' : '';
      const response = await fetch(
        `${baseUrl}/api/CustomerList?columnname=fname&direction=DESC&offset=${offset}&limit=${limit}&searchstr=${searchQuery}&cgroupid=${selectedGroupId}${filterParam}`,
        {
          headers: {
            'Authorization': authToken,
            'Content-Type': 'application/json',
          },
        }
      );
      if (!response.ok) {
        throw new Error('Failed to fetch customers');
      }
      return response.json();
    },
    staleTime: 0,
  });

  const fetchRedeemPoints = useCallback(async (customerCode: number): Promise<number> => {
    try {
      const res = await fetch(`${baseUrl}/api/GetCustPoints?customerCode=${customerCode}`, {
        headers: { Authorization: authToken, Accept: 'application/json' },
      });
      if (!res.ok) return 0;
      const json = await res.json();
      const pts = json?.data?.[0]?.BalancePoints;
      return Number(pts) || 0;
    } catch {
      return 0;
    }
  }, [baseUrl, authToken]);

  const customers: Customer[] = customersData?.data || [];
  const totalRecords = customersData?.TotalRecords || 0;
  const totalPages = pageSize === 'all' ? 1 : Math.ceil(totalRecords / parseInt(pageSize));

  // Fetch redeem points only for currently loaded customers (no additional customer list fetch)
  useEffect(() => {
    if (!customers || customers.length === 0) return;
    const missing = customers
      .map((c) => Number(c.Customercode))
      .filter((code) => Number.isFinite(code) && code > 0 && redeemPointsByCustomer[code] === undefined);
    if (missing.length === 0) return;
    let alive = true;
    (async () => {
      const entries = await Promise.all(missing.map(async (code) => [code, await fetchRedeemPoints(code)] as const));
      if (!alive) return;
      setRedeemPointsByCustomer((prev) => {
        const next = { ...prev };
        for (const [code, pts] of entries) next[code] = pts;
        return next;
      });
    })();
    return () => { alive = false; };
  }, [customers, redeemPointsByCustomer, fetchRedeemPoints]);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1);
    // Reset mobile state for new search
    if (isMobile) {
      setAllCustomers([]);
      setMobileCurrentPage(1);
      setHasMoreData(true);
      setIsLoadingMore(false);
    }
  };

  const handlePageSizeChange = (value: string) => {
    setPageSize(value);
    setCurrentPage(1);
  };

  const handleGroupChange = (value: string) => {
    const groupId = value === '0' ? 0 : parseInt(value, 10);
    setSelectedGroupId(groupId);
    setCurrentPage(1);
    // Reset mobile state for new filter
    if (isMobile) {
      setAllCustomers([]);
      setMobileCurrentPage(1);
      setHasMoreData(true);
      setIsLoadingMore(false);
    }
  };

  const handleAddCustomer = () => {
    form.reset({
      MobileNo: '',
      ContactName: '',
      CompanyName: '',
      EmailID: '',
      GSTNo: '',
      Shipping_Address: '',
      Locality: '',
      State: companyState || '',
      CGroupID: '',
      OpeningBalance: 0,
    });
    setShowAddModal(true);
  };

  const handleEdit = (customer: Customer) => {
    // Try to derive group ID: prefer CGroupID from API, else map from CGroupName
    let derivedGroupId: string = '';
    if (customer.CGroupID && !isNaN(Number(customer.CGroupID))) {
      derivedGroupId = customer.CGroupID.toString();
    } else if (customer.CGroupName) {
      const matchingGroup = groups.find(
        (g) => g.Name.toLowerCase() === customer.CGroupName!.toLowerCase()
      );
      if (matchingGroup) {
        derivedGroupId = matchingGroup.id.toString();
      }
    }

    setEditingCustomer(customer);
    updateForm.reset({
      Customercode: customer.Customercode,
      MobileNo: customer.Phone || '',
      CompanyName: customer.FName || '',
      ContactName: customer.LName || '',
      EmailID: customer.Emailid || '',
      GSTNo: customer.GSTN || '',
      Shipping_Address: customer.Address || '',
      Locality: customer.locality || '',
      State: customer.State || '',
      CGroupID: derivedGroupId,
    });
    setShowUpdateModal(true);
  };

  const handleDelete = (customer: Customer) => {
    setCustomerToDelete(customer);
    setShowDeleteDialog(true);
  };

  const onAddCustomer = async (values: CustomerFormValues) => {
    console.log('Adding Customer - Form Values:', JSON.stringify(values, null, 2));
    try {
      // Step 1: Create customer - Explicitly construct request body without OpeningBalance
      // Interchange Name and Company: send Name field as CompanyName, Company field as ContactName
      const customerRequestBody: any = {
        MobileNo: values.MobileNo,
        ContactName: values.CompanyName,
        CompanyName: values.ContactName,
        EmailID: values.EmailID,
        GSTNo: values.GSTNo,
        Shipping_Address: values.Shipping_Address,
        Locality: values.Locality,
        State: values.State
      };
      // Only include CGroupID if it is a valid number
      if (values.CGroupID && !isNaN(Number(values.CGroupID))) {
        customerRequestBody.CGroupID = Number(values.CGroupID);
      }
      
      console.log('POST /api/Customer Request:', JSON.stringify(customerRequestBody, null, 2));
      
      const res = await fetch(`${baseUrl}/api/Customer`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: authToken
        },
        body: JSON.stringify(customerRequestBody)
      });

      const responseData = await res.json();
      console.log('POST /api/Customer Response:', JSON.stringify(responseData, null, 2));

      if (!res.ok || responseData.status !== 0) {
        throw new Error(responseData.message || 'Failed to create customer');
      }

      // Step 2: Update Opening Balance if customer was created successfully
      if (responseData.data?.[0]?.CustomerCode && values.OpeningBalance !== undefined) {
        const updateOBRequestBody = {
          CustomerCode: responseData.data[0].CustomerCode,
          OpeningBalance: Number(values.OpeningBalance)
        };

        console.log('POST /api/UpdateOB Request:', JSON.stringify(updateOBRequestBody, null, 2));

        const obRes = await fetch(`${baseUrl}/api/UpdateOB`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: authToken
          },
          body: JSON.stringify(updateOBRequestBody)
        });

        const obResponseData = await obRes.json();
        console.log('POST /api/UpdateOB Response:', JSON.stringify(obResponseData, null, 2));

        if (!obRes.ok || obResponseData.status !== 0) {
          throw new Error(obResponseData.message || 'Failed to set opening balance');
        }
      }

      toast({ title: 'Success', description: responseData.message || 'Customer created successfully' });
      setShowAddModal(false);
      form.reset(); 
      queryClient.invalidateQueries({ queryKey: ['customers'] });
    } catch (e) {
      const error = e as Error;
      toast({ 
        title: 'Error', 
        description: error.message || 'Failed to create customer', 
        variant: 'destructive' 
      });
    }
  };

  const onUpdateCustomer = async (values: UpdateCustomerFormValues) => {
    try {
      // Construct payload matching API requirements
      const customerData = {
        Code: values.Customercode,
        MobileNo: values.MobileNo,
        ContactName: values.ContactName || '',
        CompanyName: values.CompanyName || '',
        EmailID: values.EmailID || '',
        GSTNo: values.GSTNo || '',
        Shipping_Address: values.Shipping_Address || '',
        Locality: values.Locality || '',
        State: values.State || '',
        CGroupID: values.CGroupID && !isNaN(Number(values.CGroupID)) ? Number(values.CGroupID) : undefined,
      };
      console.log('Updating customer with data:', JSON.stringify(customerData, null, 2));
      const res = await fetch(`${baseUrl}/api/Customer`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: authToken
        },
        body: JSON.stringify(customerData)
      });
      const responseData = await res.json();
      console.log('Customer update response:', responseData);

      // API contract: status === 0 → success; status === -1 (or others) → error
      if (!res.ok || responseData.status === -1) {
        const message =
          responseData.message ||
          (Array.isArray(responseData.errors)
            ? responseData.errors.map((e: any) => e.msg).join(', ')
            : 'Failed to update customer');
        console.error('Customer update failed:', responseData);
        throw new Error(message);
      }

      toast({
        title: 'Success',
        description: ' Customer Updated successfully',
      });
      setShowUpdateModal(false);
      setEditingCustomer(null);
      updateForm.reset();
      queryClient.invalidateQueries({ queryKey: ['customers'] });
    } catch (e) {
      console.error('Error in onUpdateCustomer:', e);
      toast({ 
        title: 'Error', 
        description: e instanceof Error ? e.message : 'Failed to update customer', 
        variant: 'destructive' 
      });
    }
  };

  const handleDeleteCustomer = async () => {
    if (!customerToDelete) return;
    try {
      const body = {
        Code: customerToDelete.Customercode
      };
      
      console.log('=== CUSTOMER DELETE API ===');
      console.log('DELETE Request URL:', `${baseUrl}/api/Customer`);
      console.log('DELETE Request Body:', JSON.stringify(body, null, 2));
      
      const res = await fetch(`${baseUrl}/api/Customer`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          Authorization: authToken
        },
        body: JSON.stringify(body)
      });
      
      const responseData = await res.json();
      console.log('DELETE Response Status:', res.status);
      console.log('DELETE Response Data:', JSON.stringify(responseData, null, 2));
      console.log('=== END CUSTOMER DELETE API ===');
      
      // Check API response status
      if (responseData.status === 0) {
        // Success case
        toast({ title: 'Success', description: 'Customer deleted successfully' });
        setShowDeleteDialog(false);
        setCustomerToDelete(null);
        queryClient.invalidateQueries({ queryKey: ['customers'] });
      } else if (responseData.status === -1) {
        // Failure case - check if it's a constraint violation
        if (responseData.message && responseData.message.includes('REFERENCE constraint')) {
          toast({ 
            title: 'Error', 
            description: 'Unable to delete the customer please see the invoices', 
            variant: 'destructive' 
          });
        } else {
          toast({ 
            title: 'Error', 
            description: responseData.message || 'Failed to delete customer', 
            variant: 'destructive' 
          });
        }
      } else {
        // Other error cases
        toast({ 
          title: 'Error', 
          description: 'Failed to delete customer', 
          variant: 'destructive' 
        });
      }
    } catch (e) {
      console.error('Customer Delete Error:', e);
      toast({ title: 'Error', description: 'Failed to delete customer', variant: 'destructive' });
    }
  };
  

  const fetchGroups = async () => {
    try {
      const response = await fetch(`${baseUrl}/api/CGroups`, {
        headers: {
          'Authorization': authToken,
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) throw new Error('Failed to fetch groups');
      const data = await response.json();
      setGroups(data.data || []);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to fetch groups', variant: 'destructive' });
    }
  };

  useEffect(() => {
    fetchGroups();
  }, []);

  const handleAddGroup = () => {
    setEditingGroup(null);
    setNewGroupName('');
    setShowGroupModal(true);
  };

  const handleEditGroup = (group: Group) => {
    setEditingGroup(group);
    setNewGroupName(group.Name);
    setShowGroupModal(true);
  };

  const handleSaveEditGroup = async () => {
    if (!newGroupName.trim()) {
      toast({ title: 'Error', description: 'Group name cannot be empty.', variant: 'destructive' });
      return;
    }
    try {
      const endpoint = `${baseUrl}/api/CGroups`;
      const body = {
        id: editingGroup ? editingGroup.id : 0,
        Name: newGroupName
      };

      const res = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: authToken
        },
        body: JSON.stringify(body)
      });

      if (!res.ok) throw new Error(`Failed to ${editingGroup ? 'update' : 'create'} group`);
      toast({ 
        title: 'Success', 
        description: editingGroup ? 'Group updated successfully' : 'Group created successfully'
      });
      setShowGroupModal(false);
      setEditingGroup(null);
      setNewGroupName('');
      fetchGroups();
    } catch (e) {
      toast({ title: 'Error', description: `Failed to ${editingGroup ? 'update' : 'create'} group`, variant: 'destructive' });
    }
  };

  // Add state for group delete confirmation
  const [groupToDelete, setGroupToDelete] = useState<Group | null>(null);
  const [showGroupDeleteDialog, setShowGroupDeleteDialog] = useState(false);

  const handleDeleteGroup = async (group: Group) => {
    setGroupToDelete(group);
    setShowGroupDeleteDialog(true);
  };

  const handleConfirmDeleteGroup = async () => {
    if (!groupToDelete) return;
    try {
      const res = await fetch(`${baseUrl}/api/DeleteCGroups`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: authToken
        },
        body: JSON.stringify({
          id: groupToDelete.id
        })
      });
      // Some APIs return 200 with an error payload. Inspect the JSON body.
      let payload: any = null;
      try {
        payload = await res.json();
      } catch (_) {
        // If body isn't JSON and HTTP failed, throw a generic error
        if (!res.ok) throw new Error('Failed to delete group');
      }

      if (!res.ok) {
        throw new Error('Failed to delete group');
      }

      if (payload && typeof payload.status !== 'undefined') {
        if (payload.status === 0) {
          toast({ title: 'Success', description: 'Success - Group deleted successfully' });
          setShowGroupDeleteDialog(false);
          setGroupToDelete(null);
          fetchGroups();
        } else {
          // status -1 or any non-zero: show API-provided message and do not delete
          toast({ title: 'Error', description: payload.message || 'Unable to delete group', variant: 'destructive' });
          setShowGroupDeleteDialog(false);
          setGroupToDelete(null);
        }
      } else {
        // Fallback: if no status, assume success only if 2xx
        toast({ title: 'Success', description: 'Success - Group deleted successfully' });
        setShowGroupDeleteDialog(false);
        setGroupToDelete(null);
        fetchGroups();
      }
    } catch (e) {
      toast({ title: 'Error', description: 'Failed to delete group', variant: 'destructive' });
    }
  };

  // Calculate the correct range of records being displayed
  const startIndex = pageSize === 'all' ? 1 : (currentPage - 1) * parseInt(pageSize) + 1;
  const endIndex = pageSize === 'all' ? totalRecords : Math.min(startIndex + customers.length - 1, totalRecords);

  // --- Add state for expanded row and transaction data ---
  const [expandedRow, setExpandedRow] = useState<number | null>(null);
  const [expandedMode, setExpandedMode] = useState<'details' | 'transactions' | null>(null);
  const [transactionData, setTransactionData] = useState<Record<number, TransactionResponse | null>>({});
  const [loadingTransactions, setLoadingTransactions] = useState<Record<number, boolean>>({});

  const handleRowClick = async (customerCode: number) => {
    if (expandedRow === customerCode && expandedMode === 'transactions') {
      setExpandedRow(null);
      setExpandedMode(null);
      return;
    }
    
    setExpandedRow(customerCode);
    setExpandedMode('transactions');
    
    if (!transactionData[customerCode]) {
      setLoadingTransactions((prev) => ({ ...prev, [customerCode]: true }));
      try {
        const res = await fetch(`${baseUrl}/api/CustomerTransactions?CustomerCode=${customerCode}&Top=10`, {
          headers: {
            'Authorization': authToken,
            'Content-Type': 'application/json',
          },
        });
        const data = await res.json();
        setTransactionData((prev) => ({ ...prev, [customerCode]: data }));
      } catch {
        setTransactionData((prev) => ({ ...prev, [customerCode]: { status: -1, message: 'Error', data: [], TotalRecords: 0 } }));
      } finally {
        setLoadingTransactions((prev) => ({ ...prev, [customerCode]: false }));
      }
    }
  };

  const handleMoreClick = (customerCode: number) => {
    if (expandedRow === customerCode && expandedMode === 'details') {
      setExpandedRow(null);
      setExpandedMode(null);
    } else {
      setExpandedRow(customerCode);
      setExpandedMode('details');
    }
  };

  const handleBack = () => {
    if (onBack) {
      onBack();
    } else {
      navigate(-1);
    }
  };

  // Custom back navigation handler for CustomerPage
  // Close form/modal first, then go back
  const handleCustomerPageBack = () => {
    if (showAddModal) {
      setShowAddModal(false);
      return;
    }
    if (showUpdateModal) {
      setShowUpdateModal(false);
      return;
    }
    if (showDeleteDialog) {
      setShowDeleteDialog(false);
      return;
    }
    if (showGroupModal) {
      setShowGroupModal(false);
      return;
    }
    if (showGroupDeleteDialog) {
      setShowGroupDeleteDialog(false);
      return;
    }
    if (showExportDialog) {
      setShowExportDialog(false);
      return;
    }
    if (onBack) {
      onBack();
    } else {
      window.dispatchEvent(new CustomEvent('setCurrentPage', { detail: 'transactions' }));
    }
  };

  // Use back navigation hook with custom handler
  // Note: This will be overridden by Dashboard's back navigation when activeSubpage is set
  useBackNavigation({
    onBack: handleCustomerPageBack,
    canGoBack: true,
    showExitConfirmation: false // Don't show exit confirmation on this page
  });

  // Function to fetch more customers for mobile infinite scroll
  const fetchMoreCustomers = useCallback(async () => {
    if (isLoadingMore || !hasMoreData) {
      console.log('📱 fetchMoreCustomers skipped:', { isLoadingMore, hasMoreData });
      return;
    }
    
    console.log('📱 fetchMoreCustomers called, page:', mobileCurrentPage);
    setIsLoadingMore(true);
    try {
      const offset = (mobileCurrentPage - 1) * 50; // Fetch 50 records per page
      console.log('📱 Fetching customers with offset:', offset);
      const response = await fetch(
        `${baseUrl}/api/CustomerList?columnname=fname&direction=DESC&offset=${offset}&limit=50&searchstr=${searchQuery}&cgroupid=${selectedGroupId}`,
        {
          headers: {
            'Authorization': authToken,
            'Content-Type': 'application/json',
          },
        }
      );
      
      if (!response.ok) {
        throw new Error('Failed to fetch more customers');
      }
      
      const data = await response.json();
      const newCustomers = data.data || [];
      console.log('📱 Fetched', newCustomers.length, 'new customers');
      
      if (newCustomers.length === 0) {
        console.log('📱 No more customers, setting hasMoreData to false');
        setHasMoreData(false);
      } else {
        setAllCustomers(prev => {
          const updated = [...prev, ...newCustomers];
          console.log('📱 Total customers now:', updated.length);
          return updated;
        });
        setMobileCurrentPage(prev => prev + 1);
      }
    } catch (error) {
      console.error('Error fetching more customers:', error);
      toast({ 
        title: 'Error', 
        description: 'Failed to load more customers', 
        variant: 'destructive' 
      });
    } finally {
      setIsLoadingMore(false);
    }
  }, [isLoadingMore, hasMoreData, mobileCurrentPage, searchQuery, selectedGroupId, baseUrl, authToken]);

  // Improved lazy loading for mobile with better performance
  useEffect(() => {
    if (window.innerWidth >= 1024 || !isMobile) return; // Only on mobile
    
    const scrollableContainer = mobileCardsContainerRef.current;
    if (!scrollableContainer) return;
    
    const handleScroll = () => {
      if (window.innerWidth >= 1024) return;
      if (!scrollableContainer) return;
      
      const { scrollTop, scrollHeight, clientHeight } = scrollableContainer;
      const scrollPercentage = (scrollTop + clientHeight) / scrollHeight;
      
      // Show scroll to top button when scrolled down
      setShowScrollToTop(scrollTop > 200);
      
      // Load more when user scrolls to 75% of the content (more responsive)
      if (scrollPercentage > 0.75 && !isLoadingMore && hasMoreData) {
        console.log('📱 Triggering lazy load at', Math.round(scrollPercentage * 100) + '%');
        fetchMoreCustomers();
      }
    };
    
    scrollableContainer.addEventListener('scroll', handleScroll, { passive: true });
    // Also trigger initial check in case content is already scrollable
    handleScroll();
    
    return () => {
      scrollableContainer.removeEventListener('scroll', handleScroll);
    };
  }, [isLoadingMore, hasMoreData, searchQuery, isMobile, fetchMoreCustomers]);

  // Track if we've initialized mobile data for current filter/search
  const initializedForFilterRef = useRef<string>('');

  // Initialize mobile data and reset when search or filter changes
  useEffect(() => {
    if (isMobile) {
      const filterKey = `${searchQuery}-${selectedGroupId}`;
      // Reset mobile state when search or filter changes
      setAllCustomers([]);
      setMobileCurrentPage(1);
      setHasMoreData(true);
      setIsLoadingMore(false);
      initializedForFilterRef.current = ''; // Reset initialization flag
    }
  }, [searchQuery, selectedGroupId, isMobile]);

  // Update mobile data when customers change (only on initial load, not when loading more)
  useEffect(() => {
    if (isMobile && !isLoadingMore && !isLoading) {
      const filterKey = `${searchQuery}-${selectedGroupId}`;
      const isInitialized = initializedForFilterRef.current === filterKey;
      
      // Only update if we haven't initialized for this filter yet
      if (!isInitialized && customers.length > 0) {
        setAllCustomers(customers);
        initializedForFilterRef.current = filterKey;
        
        // If we got a full page of results, there might be more data
        if (customers.length >= parseInt(pageSize)) {
          setMobileCurrentPage(2); // Next page will be 2
          setHasMoreData(true);
        } else {
          // If we got fewer results than page size, we've reached the end
          setHasMoreData(false);
        }
      } else if (!isInitialized && customers.length === 0) {
        setHasMoreData(false);
        initializedForFilterRef.current = filterKey;
      }
    }
  }, [customers, isMobile, isLoadingMore, isLoading, pageSize, searchQuery, selectedGroupId]);

  // Helper to determine if a column is numeric
  const numericFields = [
    'OpeningBalance', 'Total Invoiced Amount', 'Total Paid Amount', 'Total Due Amount', 'Total Invoices Made', 'Avg Invoice Value', 'Totalrecords'
  ];

  // Helper to get all unique keys except 'Customercode', ensuring all columns are included
  const getExportColumns = (data: any[]) => {
    if (!data.length) return [];
    // Collect all unique keys from all customer objects
    const allKeysSet = new Set<string>();
    data.forEach(obj => Object.keys(obj).forEach(key => allKeysSet.add(key)));
    const allKeys = Array.from(allKeysSet);
    return allKeys.filter(key => key !== 'Customercode').map(key => ({
      key: key as string,
      label: (key as string)
        .replace(/([A-Z])/g, ' $1')
        .replace(/^./, str => str.toUpperCase())
        .replace(/_/g, ' ')
    }));
  };

  const getCellValue = (customer: Record<string, any>, key: string) => {
    const value = customer[key];
    if (numericFields.includes(key.replace(/\s/g, '')) || /amount|balance|total|invoice|paid|due|number|count/i.test(key)) {
      return value === undefined || value === null || value === '' ? '0' : value;
    }
    return value === undefined || value === null ? '' : value;
  };

  const exportCustomersToExcel = async (data: any[]) => {
    try {
      // Show loading toast
      toast({
        title: "Exporting...",
        description: "Preparing customer data for export",
        className: 'bg-blue-600 text-white border-blue-700'
      });

      // Use mobile data if available, otherwise fetch all customers for export
      let customersToExport = data;
      if (isMobile && allCustomers.length > 0) {
        customersToExport = allCustomers;
      } else {
        // Fetch all customers for export (not just current page)
        const response = await fetch(
          `${baseUrl}/api/CustomerList?columnname=fname&direction=DESC&offset=0&limit=1&searchstr=${searchQuery}&cgroupid=${selectedGroupId}&filter=all`,
          {
            headers: {
              'Authorization': authToken,
              'Content-Type': 'application/json',
            },
          }
        );

        if (!response.ok) {
          throw new Error('Failed to fetch customer data for export');
        }

        const exportResponse = await response.json();
        customersToExport = exportResponse.data || [];
      }

      if (!customersToExport || customersToExport.length === 0) {
        toast({
          title: "Export Error",
          description: "No customer data to export.",
          variant: "destructive"
        });
        return;
      }
      
      const columns = getExportColumns(customersToExport);
      const exportData = customersToExport.map((customer) => {
        const row: Record<string, any> = {};
        columns.forEach(col => {
          row[col.label] = getCellValue(customer as Record<string, any>, col.key);
        });
        return row;
      });
      
      // Use the unified download utility
      downloadExcel(
        exportData,
        'customers',
        'Customers',
        () => {
          toast({
            title: "Export Success",
            description: Capacitor.isNativePlatform() 
              ? "Excel file saved to device" 
              : "Customer Excel exported successfully",
            className: 'bg-green-600 text-white border-green-700'
          });
        },
        (error) => {
          toast({
            title: "Export Error",
            description: error,
            variant: "destructive"
          });
        }
      );
    } catch (error) {
      console.error('Export error:', error);
      toast({
        title: "Export Error",
        description: error instanceof Error ? error.message : "Failed to export customers",
        variant: "destructive"
      });
    }
  };



  // Helper to sanitize values
  const sanitize = (val: any, isNumber = false) => {
    if (val === undefined || val === null || (typeof val === 'string' && val.trim() === '')) return isNumber ? '0' : 'N/A';
    if (typeof val === 'number') return val.toString();
    if (typeof val === 'string') return val.replace(/[\r\n\t]+/g, ' ').trim() || (isNumber ? '0' : 'N/A');
    return JSON.stringify(val);
  };

  // Export Customer PDF
  const exportCustomersToPDF = async (customers: any[]) => {
    try {
      // Show loading toast
      toast({
        title: "Exporting...",
        description: "Preparing customer data for PDF export",
        className: 'bg-blue-600 text-white border-blue-700'
      });

      // Fetch all customers for export (not just current page)
      const response = await fetch(
        `${baseUrl}/api/CustomerList?columnname=fname&direction=DESC&offset=0&limit=1&searchstr=${searchQuery}&cgroupid=${selectedGroupId}&filter=all`,
        {
          headers: {
            'Authorization': authToken,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch customer data for PDF export');
      }

      const exportResponse = await response.json();
      const allCustomers = exportResponse.data || [];

      if (!allCustomers || allCustomers.length === 0) {
        toast({
          title: "Export Error",
          description: "No customer data to export.",
          variant: "destructive"
        });
        return;
      }
      
      const columns = [
        { header: 'First Name', key: 'FName' },
        { header: 'Last Name', key: 'LName' },
        { header: 'Mobile', key: 'Phone' },
        { header: 'Email', key: 'Emailid' },
        { header: 'GSTN', key: 'GSTN' },
        { header: 'Address', key: 'Address' },
        { header: 'Location', key: 'locality' },
        { header: 'State', key: 'State' },
        { header: 'Opening Balance', key: 'OpeningBalance', isNumber: true },
      ];
      
      const rows = allCustomers.map(cust =>
        columns.map(col => sanitize(cust[col.key], col.isNumber))
      );
      
      const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'A4' });
      autoTable(doc, {
        head: [columns.map(col => col.header)],
        body: rows,
        styles: {
          fontSize: 10,
          cellPadding: 4,
          halign: 'center',
          valign: 'middle',
          textColor: 20,
          fontStyle: 'bold'
        },
        headStyles: {
          fillColor: [30, 30, 30],
          textColor: 255,
          fontStyle: 'bold',
          halign: 'center',
          valign: 'middle',
          fontSize: 11
        },
        bodyStyles: {
          fontStyle: 'bold',
          textColor: 20,
          halign: 'center',
          valign: 'middle'
        },
        tableWidth: 'auto',
        margin: { left: 0, right: 0 },
        startY: 40
      });
      
      // Use the unified download utility
      const pdfDataUri = doc.output('datauristring');
      downloadPdf(
        pdfDataUri,
        'CustomerDetails',
        () => {
          toast({
            title: "Export Success",
            description: Capacitor.isNativePlatform() 
              ? "PDF saved to device and share dialog opened" 
              : "Customer PDF exported successfully",
            className: 'bg-green-600 text-white border-green-700'
          });
        },
        (error) => {
          toast({
            title: "Export Error",
            description: error,
            variant: "destructive"
          });
        }
      );
    } catch (error) {
      console.error('PDF export error:', error);
      toast({
        title: "Export Error",
        description: error instanceof Error ? error.message : "Failed to export customers to PDF",
        variant: "destructive"
      });
    }
  };



  // Share Recent Transactions PDF


  // Export Recent Transactions PDF
  const exportTransactionsToPDF = (customers: any[], transactionData: Record<number, any>) => {
    if (!customers || customers.length === 0) {
      toast({
        title: "Export Error",
        description: "No customer data to export.",
        variant: "destructive"
      });
      return;
    }
    // Define columns for transactions
    const columns = [
      { header: 'Customer Name', key: 'customerName' },
      { header: 'Mobile Number', key: 'Phone' },
      { header: 'Date', key: 'Date' },
      { header: 'Tx Type', key: 'Tx Type' },
      { header: 'Invoice Amt', key: 'Invoice Amt', isNumber: true },
      { header: 'Payment', key: 'Payment', isNumber: true },
      { header: 'Due Amount', key: 'Due Amount', isNumber: true },
    ];
    const rows: string[][] = [];
    customers.forEach(cust => {
      const txs = (transactionData && transactionData[cust.Customercode]?.data) || [];
      if (txs.length > 0) {
        txs.forEach(tx => {
          rows.push([
            `${sanitize(cust.FName)} ${sanitize(cust.LName)}`,
            sanitize(cust.Phone),
            sanitize(tx.Date),
            sanitize(tx['Tx Type']),
            sanitize(tx['Invoice Amt'], true),
            sanitize(tx.Payment, true),
            sanitize(tx['Due Amount'], true)
          ]);
        });
      } else {
        // If no transactions, show a single row with N/A/0 for transaction fields
        rows.push([
          `${sanitize(cust.FName)} ${sanitize(cust.LName)}`,
          sanitize(cust.Phone),
          'N/A', 'N/A', '0', '0', '0'
        ]);
      }
    });
    const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'A4' });
    autoTable(doc, {
      head: [columns.map(col => col.header)],
      body: rows,
      styles: {
        fontSize: 10,
        cellPadding: 4,
        halign: 'center',
        valign: 'middle',
        textColor: 20,
        fontStyle: 'bold'
      },
      headStyles: {
        fillColor: [30, 30, 30],
        textColor: 255,
        fontStyle: 'bold',
        halign: 'center',
        valign: 'middle',
        fontSize: 11
      },
      bodyStyles: {
        fontStyle: 'bold',
        textColor: 20,
        halign: 'center',
        valign: 'middle'
      },
      tableWidth: 'auto',
      margin: { left: 0, right: 0 },
      startY: 40
    });
    
    // Use the unified download utility
    const pdfDataUri = doc.output('datauristring');
    downloadPdf(
      pdfDataUri,
      'RecentTransactions',
      () => {
        toast({
          title: "Export Success",
          description: Capacitor.isNativePlatform() 
            ? "PDF saved to device and share dialog opened" 
            : "Transactions PDF exported successfully",
          className: 'bg-green-600 text-white border-green-700'
        });
      },
      (error) => {
        toast({
          title: "Export Error",
          description: error,
          variant: "destructive"
        });
      }
    );
  };

  const allKeys = Array.from(
    new Set(customers.flatMap(customer => Object.keys(customer)))
  );

  const allColumns = allKeys.map(key => ({
    key,
    label: key.charAt(0).toUpperCase() + key.slice(1) // or use a mapping for prettier labels
  }));

  const fetchStates = async () => {
    const response = await fetch(`${baseUrl}/api/States`, {
      headers: {
        'Authorization': authToken,
        'Content-Type': 'application/json',
      },
    });
    if (!response.ok) throw new Error('Failed to fetch states');
    return response.json();
  };

  const { data: statesResponse, isLoading: isStatesLoading, error: statesError } = useQuery({
    queryKey: ['customer-states'],
    queryFn: fetchStates,
    staleTime: 1000 * 60 * 60, // 1 hour
  });
  const states = statesResponse?.data || [];

  // Cleanup effect to clear cache when component unmounts
  useEffect(() => {
    return () => {
      // Clear customer specific queries when component unmounts
      queryClient.removeQueries({ queryKey: ['customers'] });
      queryClient.removeQueries({ queryKey: ['customer-states'] });
    };
  }, [queryClient]);

  // Toggle customer selection for ledger
  const toggleCustomerSelection = (customerCode: number) => {
    setSelectedCustomerCodes(prev => {
      const newSet = new Set(prev);
      if (newSet.has(customerCode)) {
        newSet.delete(customerCode);
      } else {
        newSet.add(customerCode);
      }
      return newSet;
    });
  };

  // Select/Deselect all visible customers
  const toggleSelectAll = () => {
    const visibleCustomerCodes = (isMobile ? allCustomers : customers).map(c => c.Customercode);
    if (selectedCustomerCodes.size === visibleCustomerCodes.length) {
      setSelectedCustomerCodes(new Set());
    } else {
      setSelectedCustomerCodes(new Set(visibleCustomerCodes));
    }
  };

  // Generate Ledger PDF
  const generateCustomerLedgerPDF = async () => {
    if (selectedCustomerCodes.size === 0) {
      toast({
        title: "No Customers Selected",
        description: "Please select at least one customer to generate the ledger",
        variant: "destructive"
      });
      return;
    }

    if (!ledgerStartDate || !ledgerEndDate) {
      toast({
        title: "Date Range Required",
        description: "Please select start and end dates for the ledger",
        variant: "destructive"
      });
      return;
    }

    setIsGeneratingLedger(true);

    try {
      const doc = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'A4' });
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();

      // Format dates for display (DD-MM-YYYY)
      const formatDateDisplay = (dateStr: string) => {
        const [year, month, day] = dateStr.split('-');
        return `${day}-${month}-${year}`;
      };

      // Format dates for API (DD-MM-YYYY)
      const formatDateForAPI = (dateStr: string) => {
        const [year, month, day] = dateStr.split('-');
        return `${day}-${month}-${year}`;
      };

      const startDateDisplay = formatDateDisplay(ledgerStartDate);
      const endDateDisplay = formatDateDisplay(ledgerEndDate);

      // Hard-coded company details
      const COMPANY_NAME = 'BEST TRADING COMPANY';
      const COMPANY_ADDRESS_LINE1 = '35-2-888, Jawahar Colony Road,Gopalpur';
      const COMPANY_ADDRESS_LINE2 = 'Hanamkonda, Telangana, 506009';
      const COMPANY_CONTACT_LINE = 'Phone : 9849492466, GST : 33AMJPN0755E1Z9';

      let isFirstCustomer = true;

      // Fetch and generate ledger for each selected customer
      for (const customerCode of Array.from(selectedCustomerCodes)) {
        const customer = (isMobile ? allCustomers : customers).find(c => c.Customercode === customerCode);
        if (!customer) continue;

        // Add new page for each customer (except the first)
        if (!isFirstCustomer) {
          doc.addPage();
        }
        isFirstCustomer = false;

        // Fetch customer ledger data
        const endpoint = `${baseUrl}/api/CustInvoicesPayments?CustomerCode=${customerCode}&StartDate=${formatDateForAPI(ledgerStartDate)}&EndDate=${formatDateForAPI(ledgerEndDate)}`;
        
        const response = await fetch(endpoint, {
          headers: {
            'Authorization': authToken,
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch data for customer ${customer.FName}`);
        }

        const data = await response.json();
        
        const openingBalance = data.data?.OpeningBalance || 0;
        const closingBalance = data.data?.ClosingBalance || 0;
        const invoices = data.data?.Invoices || [];

        // Draw header
        const drawHeader = (startY: number) => {
          let currentY = startY;

          // Logo box placeholder (top left)
          doc.setDrawColor(0, 0, 0);
          doc.setLineWidth(1);
          doc.rect(40, currentY, 60, 60); // Box for logo
          doc.setFontSize(6);
          doc.setFont('helvetica', 'normal');
          doc.text('LOGO', 55, currentY + 33);

          // Company Name (centered, bold)
          doc.setFontSize(14);
          doc.setFont('helvetica', 'bold');
          const companyNameWidth = doc.getTextWidth(COMPANY_NAME);
          doc.text(COMPANY_NAME, (pageWidth - companyNameWidth) / 2, currentY + 20);

          // Company Address Line 1 (centered)
          doc.setFontSize(8);
          doc.setFont('helvetica', 'normal');
          const addr1Width = doc.getTextWidth(COMPANY_ADDRESS_LINE1);
          doc.text(COMPANY_ADDRESS_LINE1, (pageWidth - addr1Width) / 2, currentY + 33);

          // Company Address Line 2 (centered)
          const addr2Width = doc.getTextWidth(COMPANY_ADDRESS_LINE2);
          doc.text(COMPANY_ADDRESS_LINE2, (pageWidth - addr2Width) / 2, currentY + 43);

          // Company Contact Line (centered)
          const contactWidth = doc.getTextWidth(COMPANY_CONTACT_LINE);
          doc.text(COMPANY_CONTACT_LINE, (pageWidth - contactWidth) / 2, currentY + 53);

          currentY += 70;

          // Customer Name (left-aligned, bold)
          doc.setFontSize(10);
          doc.setFont('helvetica', 'bold');
          const customerName = `${customer.FName || ''} ${customer.LName || ''}`.trim();
          doc.text(customerName, 40, currentY);
          currentY += 15;

          // Separator line
          doc.line(40, currentY, pageWidth - 40, currentY);
          currentY += 10;

          // Ledger Period (centered, bold)
          doc.setFontSize(9);
          doc.setFont('helvetica', 'bold');
          const ledgerTitle = `Ledger of NITIN`;
          const titleWidth = doc.getTextWidth(ledgerTitle);
          doc.text(ledgerTitle, (pageWidth - titleWidth) / 2, currentY);
          currentY += 12;

          // Date Range (centered)
          doc.setFontSize(9);
          doc.setFont('helvetica', 'bold');
          const dateRange = `Between ${startDateDisplay} and ${endDateDisplay}`;
          const dateWidth = doc.getTextWidth(dateRange);
          doc.text(dateRange, (pageWidth - dateWidth) / 2, currentY);
          currentY += 15;

          return currentY;
        };

        // Draw header and get starting Y position
        const tableStartY = drawHeader(30);

        // Summary table data
        const summaryData = [
          ['Opening Balance', 'Total Invoiced Amount', 'Total Paid Amount', 'Total Due Amount', 'Invoiced Between Dates', 'Paid During Dates'],
          [
            openingBalance.toFixed(2),
            (data.data?.TotalInvoicedAmount || 0).toFixed(2),
            (data.data?.TotalPaidAmount || 0).toFixed(2),
            (data.data?.TotalDueAmount || 0).toFixed(2),
            (data.data?.InvoicedBetweenDates || 0).toFixed(2),
            (data.data?.PaidDuringDates || 0).toFixed(2)
          ]
        ];

        // Add summary table
        autoTable(doc, {
          startY: tableStartY,
          head: [summaryData[0]],
          body: [summaryData[1]],
          theme: 'grid',
          styles: {
            fontSize: 7,
            halign: 'center',
            valign: 'middle',
            textColor: [0, 0, 0],
            lineColor: [0, 0, 0],
            lineWidth: 0.5,
          },
          headStyles: {
            fillColor: [255, 255, 255],
            textColor: [0, 0, 0],
            fontStyle: 'bold',
            fontSize: 7,
          },
          bodyStyles: {
            fillColor: [255, 255, 255],
            fontStyle: 'bold',
            fontSize: 7,
          },
          margin: { left: 40, right: 40 },
        });

        const invoiceTableStartY = (doc as any).lastAutoTable.finalY + 20;

        // Invoice list header
        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.text('List of all Invoices', 40, invoiceTableStartY);

        // Prepare invoice table data
        const invoiceTableData: any[] = [];
        
        invoices.forEach((invoiceItem: any) => {
          const invoice = invoiceItem.Invoice;
          const payments = invoiceItem.Payments || [];

          // Add invoice row
          invoiceTableData.push([
            formatDateDisplay(invoice.InvoiceDate?.split('T')[0] || ''),
            invoice.InvoiceAmount?.toFixed(2) || '0.00',
            invoice.CompanyName || '',
            invoice.InvoiceNumber || '',
            invoice.Location || ''
          ]);

          // Add payment rows if any
          if (payments.length > 0) {
            payments.forEach((payment: any) => {
              invoiceTableData.push([
                formatDateDisplay(payment.PaidOn?.split('T')[0] || ''),
                payment.PaidAmount?.toFixed(2) || '0.00',
                payment.ReceiptNo || '',
                payment.Type || '',
                payment.InvoiceNo || ''
              ]);
            });
          }
        });

        // Add invoice table
        autoTable(doc, {
          startY: invoiceTableStartY + 15,
          head: [['Invoice Date', 'Invoiced Amount', 'Company Name', 'Invoice Number', 'Location']],
          body: invoiceTableData,
          theme: 'grid',
          styles: {
            fontSize: 7,
            halign: 'center',
            valign: 'middle',
            textColor: [0, 0, 0],
            lineColor: [0, 0, 0],
            lineWidth: 0.5,
          },
          headStyles: {
            fillColor: [255, 255, 255],
            textColor: [0, 0, 0],
            fontStyle: 'bold',
            fontSize: 7,
          },
          bodyStyles: {
            fillColor: [255, 255, 255],
            fontSize: 6,
          },
          margin: { left: 40, right: 40 },
          showHead: 'everyPage',
          didDrawPage: (data: any) => {
            // Draw header on new pages
            if (data.pageNumber > 1) {
              drawHeader(30);
            }

            // Add page number at bottom left
            const pageCount = doc.getNumberOfPages();
            doc.setFontSize(8);
            doc.setFont('helvetica', 'normal');
            doc.text(`Page ${data.pageNumber} of ${pageCount}`, 40, pageHeight - 20);
          },
        });

        // Add "Payments Received" section if there are payments
        const allPayments: any[] = [];
        invoices.forEach((invoiceItem: any) => {
          const payments = invoiceItem.Payments || [];
          allPayments.push(...payments);
        });

        if (allPayments.length > 0) {
          const paymentTableStartY = (doc as any).lastAutoTable.finalY + 20;

          // Payment header
          doc.setFontSize(10);
          doc.setFont('helvetica', 'bold');
          doc.text('Payments Received', 40, paymentTableStartY);

          // Prepare payment table data
          const paymentTableData = allPayments.map((payment: any) => [
            formatDateDisplay(payment.PaidOn?.split('T')[0] || ''),
            payment.PaidAmount?.toFixed(2) || '0.00',
            payment.ReceiptNo || '',
            payment.Type || '',
            payment.InvoiceNo || ''
          ]);

          // Add payment table
          autoTable(doc, {
            startY: paymentTableStartY + 15,
            head: [['Paid On', 'Paid Amount', 'Receipt No.', 'Type', 'Invoice No']],
            body: paymentTableData,
            theme: 'grid',
            styles: {
              fontSize: 7,
              halign: 'center',
              valign: 'middle',
              textColor: [0, 0, 0],
              lineColor: [0, 0, 0],
              lineWidth: 0.5,
            },
            headStyles: {
              fillColor: [255, 255, 255],
              textColor: [0, 0, 0],
              fontStyle: 'bold',
              fontSize: 7,
            },
            bodyStyles: {
              fillColor: [255, 255, 255],
              fontSize: 6,
            },
            margin: { left: 40, right: 40 },
            didDrawPage: (data: any) => {
              // Draw header on new pages
              if (data.pageNumber > 1) {
                drawHeader(30);
              }

              // Add page number at bottom left
              const pageCount = doc.getNumberOfPages();
              doc.setFontSize(8);
              doc.setFont('helvetica', 'normal');
              doc.text(`Page ${data.pageNumber} of ${pageCount}`, 40, pageHeight - 20);
            },
          });
        }
      }

      // Update page numbers on all pages
      const totalPages = doc.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setFont('helvetica', 'normal');
        // Clear previous page number and redraw
        doc.setTextColor(255, 255, 255);
        doc.text(`Page ${i} of `, 40, pageHeight - 20);
        doc.setTextColor(0, 0, 0);
        doc.text(`Page ${i} of ${totalPages}`, 40, pageHeight - 20);
      }

      // Download PDF
      const pdfDataUri = doc.output('datauristring');
      downloadPdf(
        pdfDataUri,
        `CustomerLedger_${startDateDisplay}_${endDateDisplay}`,
        () => {
          toast({
            title: "Export Success",
            description: Capacitor.isNativePlatform()
              ? "PDF saved to device"
              : "Customer ledger PDF exported successfully",
            className: 'bg-green-600 text-white border-green-700'
          });
        },
        (error) => {
          toast({
            title: "Export Error",
            description: error,
            variant: "destructive"
          });
        }
      );
    } catch (error) {
      console.error('Ledger PDF generation error:', error);
      toast({
        title: "Export Error",
        description: error instanceof Error ? error.message : "Failed to generate customer ledger",
        variant: "destructive"
      });
    } finally {
      setIsGeneratingLedger(false);
    }
  };

  // No permission: user must have View Customer (9) to see customer tab and list
  if (!canViewCustomer) {
    return (
      <div className="lg:space-y-2 pt-0 px-2 sm:px-4 md:px-6 lg:px-8 lg:pb-6 lg:pt-2">
        {isMobile && (
          <AppBar title="Customer Management" onBack={handleCustomerPageBack} className="lg:hidden" />
        )}
        <div className="flex-1 flex flex-col items-center justify-center gap-4 p-4 min-h-[50vh]">
          <p className="text-muted-foreground text-center">You don&apos;t have permission to view customers.</p>
          <Button onClick={handleCustomerPageBack} variant="outline" className="flex items-center gap-2">
            <ArrowLeft className="h-4 w-4" /> Back
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="lg:space-y-2 pt-0 px-2 sm:px-4 md:px-6 lg:px-8 lg:pb-6 lg:pt-2 android-form-container md:max-w-4xl lg:max-w-5xl xl:max-w-6xl 2xl:max-w-7xl md:mx-auto">
      {/* Mobile AppBar with 3-dots menu */}
      {isMobile && (
        <AppBar 
          title="Customer Management" 
          onBack={onBack}
          className="lg:hidden"
        >
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-10 w-10 text-white hover:bg-white/20">
                <MoreVertical className="h-5 w-5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-40">
              <DropdownMenuItem onClick={() => setShowExportDialog(true)}>
                Export to PDF
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => exportCustomersToExcel(customers)}>
                Download Excel
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </AppBar>
      )}

      {/* Desktop header section - Sticky and fixed */}
      <div className="hidden lg:block lg:sticky lg:top-0 lg:z-50 lg:bg-white lg:border-b lg:shadow-sm lg:space-y-3 lg:pt-3 lg:pb-3 lg:px-0">
        <div className="flex items-center gap-3 flex-nowrap">
          {onBack && (
            <div className="flex-shrink-0">
              <Button
                onClick={onBack}
                variant="outline"
                className="bg-white text-black hover:bg-gray-100 w-fit px-4 py-2 flex items-center group"
              >
                <ArrowLeft className="h-4 w-4 mr-2 md:mr-0 group-hover:mr-2 transition-all duration-200" />
                <span className="hidden md:inline group-hover:inline">Back</span>
              </Button>
            </div>
          )}
          <h1 className="text-lg sm:text-xl font-bold whitespace-nowrap flex-shrink-0">
            Customer Management
          </h1>
          {/* Desktop primary actions aligned right after heading */}
          <div className="flex items-center gap-2 flex-nowrap flex-shrink-0">
            <Button
              onClick={handleAddGroup}
              className="bg-blue-600 hover:bg-blue-700 text-white min-w-[130px] flex-shrink-0"
            >
              <Plus className="w-4 h-4 mr-2" />
              Manage Groups
            </Button>
            {canCreateCustomer && (
              <Button
                onClick={handleAddCustomer}
                className="bg-green-600 hover:bg-green-700 text-white min-w-[130px] flex-shrink-0"
              >
                <UserPlus className="w-4 h-4 mr-2" />
                Add Customer
              </Button>
            )}
            <Button
              onClick={() => setShowExportDialog(true)}
              className="min-w-[130px] flex-shrink-0"
            >
              Export to PDF
            </Button>
            <Button
              variant="outline"
              onClick={() => exportCustomersToExcel(customers)}
              className="border-green-600 text-green-600 hover:bg-green-50 border-2 shadow-sm hover:shadow-md transition-all duration-200 min-w-[130px] flex-shrink-0"
            >
              Download Excel
            </Button>
          </div>
        </div>

        {/* Ledger Controls Row */}
        <div className="flex items-center gap-3 w-full border-t pt-3">
          <div className="flex items-center gap-2">
            <Label className="text-sm font-medium whitespace-nowrap">Start Date:</Label>
            <Input
              type="date"
              value={ledgerStartDate}
              onChange={(e) => setLedgerStartDate(e.target.value)}
              className="w-[150px]"
            />
          </div>
          <div className="flex items-center gap-2">
            <Label className="text-sm font-medium whitespace-nowrap">End Date:</Label>
            <Input
              type="date"
              value={ledgerEndDate}
              onChange={(e) => setLedgerEndDate(e.target.value)}
              className="w-[150px]"
            />
          </div>
        </div>

        <div className="flex items-center gap-3 w-full mt-2">
          <div className="relative flex-1 flex items-center max-w-xl">
            <Input
              ref={searchInputRef}
              placeholder="Search customers..."
              value={searchQuery}
              onChange={handleSearch}
              className="w-full pr-10"
            />
            {/* Clear search button - shift left to make space for + on mobile */}
            {searchQuery && (
              <button
                type="button"
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-xl font-bold"
                onClick={() => {
                  setSearchQuery('');
                  searchInputRef.current?.blur();
                }}
                aria-label="Clear search"
              >
                ×
              </button>
            )}
          </div>
          {/* Total Customers count - shown between search and dropdown on desktop */}
          <div className="hidden lg:flex items-center gap-2 px-3 py-1.5 bg-gray-50 rounded-md border">
            <span className="text-sm font-medium text-gray-700">Total Customers:</span>
            <span className="text-sm font-semibold text-gray-900">{totalRecords.toLocaleString()}</span>
          </div>
          <div className="hidden lg:block">
            <Select value={selectedGroupId.toString()} onValueChange={handleGroupChange}>
              <SelectTrigger className="w-[160px]">
                <SelectValue placeholder="All Groups" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="0">All</SelectItem>
                {groups.map((group) => (
                  <SelectItem key={group.id} value={group.id.toString()}>
                    {group.Name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="hidden lg:block">
            <Select value={pageSize} onValueChange={handlePageSizeChange}>
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder="Select page size" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="10">10 per page</SelectItem>
                <SelectItem value="25">25 per page</SelectItem>
                <SelectItem value="50">50 per page</SelectItem>
                <SelectItem value="100">100 per page</SelectItem>
                <SelectItem value="all">All</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Mobile fixed search and actions section */}
      {isMobile && (
        <div className="lg:hidden fixed top-14 left-0 right-0 z-40 bg-white border-b border-gray-200 px-4 py-3">
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <Input
                ref={searchInputRef}
                placeholder="Search customers..."
                value={searchQuery}
                onChange={handleSearch}
                className="w-full pr-10"
              />
              {searchQuery && (
                <button
                  type="button"
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-xl font-bold"
                  onClick={() => {
                    setSearchQuery('');
                    searchInputRef.current?.blur();
                  }}
                  aria-label="Clear search"
                >
                  ×
                </button>
              )}
            </div>
            <Popover>
              <PopoverTrigger asChild>
                <button
                  aria-label="Quick actions"
                  className="h-10 w-10 rounded-md bg-white text-gray-700 border border-gray-200 shadow-sm hover:shadow-md active:scale-95 transition-all flex items-center justify-center"
                >
                  <Plus className="h-5 w-5 text-blue-600" />
                </button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-56 p-2 bg-white border shadow-lg rounded-xl">
                <div className="space-y-2">
                  {canCreateCustomer && (
                    <Button
                      onClick={handleAddCustomer}
                      className="w-full bg-green-600 hover:bg-green-700 text-white shadow-sm"
                    >
                      <UserPlus className="w-4 h-4 mr-2" />
                      Add Customer
                    </Button>
                  )}
                  <Button
                    onClick={handleAddGroup}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white shadow-sm"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Manage Groups
                  </Button>
                </div>
              </PopoverContent>
            </Popover>
          </div>
          {/* Mobile Group Filter */}
          <div className="mt-2">
            <Select value={selectedGroupId.toString()} onValueChange={handleGroupChange}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="All Groups" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="0">All</SelectItem>
                {groups.map((group) => (
                  <SelectItem key={group.id} value={group.id.toString()}>
                    {group.Name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {/* Mobile Ledger Controls */}
          <div className="mt-2 space-y-2">
            <div className="flex gap-2">
              <div className="flex-1">
                <Label className="text-xs mb-1 block">Start Date</Label>
                <Input
                  type="date"
                  value={ledgerStartDate}
                  onChange={(e) => setLedgerStartDate(e.target.value)}
                  className="w-full"
                />
              </div>
              <div className="flex-1">
                <Label className="text-xs mb-1 block">End Date</Label>
                <Input
                  type="date"
                  value={ledgerEndDate}
                  onChange={(e) => setLedgerEndDate(e.target.value)}
                  className="w-full"
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Total customer count display - Only shown on mobile, desktop shows inline */}
      <div className={`flex items-center justify-between px-4 py-1.5 bg-gray-50 rounded-lg border ${isMobile ? 'fixed top-64 left-0 right-0 z-30 mx-4' : 'hidden'}`}>
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-gray-700">Total Customers:</span>
          <span className="text-lg font-semibold text-gray-900">{totalRecords.toLocaleString()}</span>
        </div>
        {searchQuery && (
          <div className="text-sm text-gray-500">
            Showing {customers.length} of {totalRecords} customers
          </div>
        )}
      </div>

      {/* Responsive Customer List: Table for md+ screens, cards for small screens */}
      {/* Table view for >768px screens */}
      <div className="hidden lg:block rounded-md border overflow-x-auto max-h-[calc(100vh-260px)] scrollbar-hide">
        <Table>
          <TableHeader className="sticky top-0 z-30 bg-white">
            <TableRow>
              <TableHead className="whitespace-nowrap px-3 py-2 text-center align-middle font-semibold bg-white w-12">
                <div 
                  onClick={toggleSelectAll}
                  className="cursor-pointer text-sm text-blue-600 hover:text-blue-800 font-medium mb-1"
                >
                  {selectedCustomerCodes.size === customers.length && customers.length > 0 ? 'Deselect All' : 'All'}
                </div>
                <input
                  type="checkbox"
                  checked={selectedCustomerCodes.size === customers.length && customers.length > 0}
                  onChange={toggleSelectAll}
                  className="w-4 h-4 cursor-pointer"
                />
              </TableHead>
              <TableHead className="whitespace-nowrap px-3 py-2 text-left align-middle font-semibold bg-white"> </TableHead>
              <TableHead className="whitespace-nowrap px-3 py-2 text-center align-middle font-semibold bg-white w-16">S.No</TableHead>
              <TableHead className="whitespace-nowrap px-3 py-2 text-left align-middle font-semibold bg-white">Name</TableHead>
              <TableHead className="whitespace-nowrap px-3 py-2 text-left align-middle font-semibold bg-white">Company</TableHead>
              <TableHead className="whitespace-nowrap px-3 py-2 text-left align-middle font-semibold bg-white">Mobile</TableHead>
              <TableHead className="whitespace-nowrap px-3 py-2 text-left align-middle font-semibold bg-white">Email</TableHead>
              <TableHead className="whitespace-nowrap px-3 py-2 text-left align-middle font-semibold bg-white">Group</TableHead>
              <TableHead className="whitespace-nowrap px-3 py-2 text-left align-middle font-semibold bg-white">First Sale</TableHead>
              <TableHead className="whitespace-nowrap px-3 py-2 text-left align-middle font-semibold bg-white">Last Sale</TableHead>
              <TableHead className="whitespace-nowrap px-3 py-2 text-left align-middle font-semibold bg-white">Days</TableHead>
              <TableHead className="whitespace-nowrap px-3 py-2 text-left align-middle font-semibold bg-white">Opening Balance</TableHead>
              <TableHead className="whitespace-nowrap px-3 py-2 text-left align-middle font-semibold bg-white">Invoiced Amount</TableHead>
              <TableHead className="whitespace-nowrap px-3 py-2 text-left align-middle font-semibold bg-white">Paid Amount</TableHead>
              <TableHead className="whitespace-nowrap px-3 py-2 text-left align-middle font-semibold bg-white">Due Amount</TableHead>
              <TableHead className="whitespace-nowrap px-3 py-2 text-left align-middle font-semibold bg-white">Redeem Points</TableHead>
              <TableHead className="whitespace-nowrap px-3 py-2 text-left align-middle font-semibold bg-white">Invoices</TableHead>
              <TableHead className="whitespace-nowrap px-3 py-2 text-left align-middle font-semibold bg-white">GSTN</TableHead>
              <TableHead className="whitespace-nowrap px-3 py-2 text-left align-middle font-semibold bg-white">Address</TableHead>
              <TableHead className="whitespace-nowrap px-3 py-2 text-left align-middle font-semibold bg-white">State</TableHead>
              <TableHead className="whitespace-nowrap px-3 py-2 text-left align-middle font-semibold bg-white">Avg Value</TableHead>
              <TableHead className="whitespace-nowrap px-3 py-2 text-right align-middle font-semibold sticky right-0 bg-white z-10">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody className="overflow-y-auto">
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={21} className="text-center">
                  <div className="flex items-center justify-center">
                    <Loader2 className="h-6 w-6 animate-spin" />
                    <span className="ml-2">Loading...</span>
                  </div>
                </TableCell>
              </TableRow>
            ) : !canViewCustomer ? (
              <TableRow>
                <TableCell colSpan={21} className="text-center py-8 text-gray-500">
                  You do not have permission to view customer data.
                </TableCell>
              </TableRow>
            ) : customers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={21} className="text-center">
                  No customers found
                </TableCell>
              </TableRow>
            ) : (
              customers.map((customer, index) => {
                const isExpanded = expandedRow === customer.Customercode;
                const showDetails = isExpanded && expandedMode === 'details';
                const showTransactions = isExpanded && expandedMode === 'transactions';
                // Calculate serial number based on current page and position
                const serialNumber = pageSize === 'all' 
                  ? index + 1 
                  : (currentPage - 1) * parseInt(pageSize) + index + 1;
                
                return (
                  <React.Fragment key={customer.Customercode}>
                    <TableRow key={customer.Customercode} className="group text-sm">
                      {/* Checkbox */}
                      <TableCell className="align-middle px-3 text-center">
                        <input
                          type="checkbox"
                          checked={selectedCustomerCodes.has(customer.Customercode)}
                          onChange={() => toggleCustomerSelection(customer.Customercode)}
                          className="w-4 h-4 cursor-pointer"
                        />
                      </TableCell>
                      {/* Chevron */}
                      <TableCell className="align-middle px-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleRowClick(customer.Customercode)}
                          className={
                            `transition-all duration-200 rounded-full hover:bg-gray-100 focus:outline-none ${showTransactions ? 'bg-gray-100' : ''}`
                          }
                          aria-label={showTransactions ? 'Hide transactions' : 'Show transactions'}
                        >
                          <ChevronRight
                            className={
                              `h-5 w-5 transition-transform duration-200 ease-in-out ${showTransactions ? 'rotate-90' : ''}`
                            }
                          />
                        </Button>
                      </TableCell>
                      {/* Serial Number */}
                      <TableCell className="px-3 py-1.5 text-center align-middle font-medium text-gray-700 w-16">
                        {serialNumber}
                      </TableCell>
                      <TableCell className="px-3 py-1.5 align-middle break-words">{customer.FName}</TableCell>
                      <TableCell className="px-3 py-1.5 align-middle break-words">{customer.LName}</TableCell>
                      <TableCell className="whitespace-nowrap px-3 py-1.5 align-middle">{customer.Phone}</TableCell>
                      <TableCell className="whitespace-nowrap px-3 py-1.5 align-middle">{customer.Emailid}</TableCell>
                      <TableCell className="whitespace-nowrap px-3 py-1.5 align-middle">
                        {customer.CGroupName && customer.CGroupName.trim() !== '' ? customer.CGroupName : '-'}
                      </TableCell>
                      <TableCell className="whitespace-nowrap px-3 py-1.5 align-middle">{formatDate(customer["First sale date"])}</TableCell>
                      <TableCell className="whitespace-nowrap px-3 py-1.5 align-middle">{formatDate(customer["Last sale date"])}</TableCell>
                      <TableCell className="whitespace-nowrap px-3 py-1.5 align-middle">{customer["Number of days till today"] ?? 'N/A'}</TableCell>
                      <TableCell className="whitespace-nowrap px-3 py-1.5 text-left align-middle">
                        {customer.OpeningBalance?.toLocaleString('en-IN', {
                          style: 'currency',
                          currency: 'INR',
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2
                        }) || '₹0.00'}
                      </TableCell>
                      <TableCell className="whitespace-nowrap px-3 py-1.5 align-middle">₹{(customer["Total Invoiced Amount"] ?? 0).toFixed(2)}</TableCell>
                      <TableCell className="whitespace-nowrap px-3 py-1.5 align-middle">₹{(customer["Total Paid Amount"] ?? 0).toFixed(2)}</TableCell>
                      <TableCell className="whitespace-nowrap px-3 py-1.5 align-middle">₹{(customer["Total Due Amount"] ?? 0).toFixed(2)}</TableCell>
                      <TableCell className="whitespace-nowrap px-3 py-1.5 align-middle">
                        {(redeemPointsByCustomer[customer.Customercode] ?? 0).toFixed(2)}
                      </TableCell>
                      <TableCell className="whitespace-nowrap px-3 py-1.5 align-middle">{customer["Total Invoices Made"] ?? 0}</TableCell>
                      <TableCell className="whitespace-nowrap px-3 py-1.5 align-middle">{customer.GSTN}</TableCell>
                      <TableCell className="whitespace-nowrap px-3 py-1.5 align-middle">{customer.Address}</TableCell>
                      <TableCell className="whitespace-nowrap px-3 py-1.5 align-middle">{customer.State}</TableCell>
                      <TableCell className="whitespace-nowrap px-3 py-1.5 align-middle">₹{(customer["Avg Invoice Value"] ?? 0).toFixed(2)}</TableCell>
                      <TableCell className="whitespace-nowrap px-3 py-1.5 text-right align-middle sticky right-0 bg-white z-10">
                        <div className="flex justify-end gap-2">
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={() => { setSelectedCustomer(customer); setShowWhatsAppModal(true); }}
                            className="text-green-600 hover:text-green-700 flex items-center"
                            title="WhatsApp"
                          >
                            <FaWhatsapp className="h-5 w-5" />
                          </Button>
                          <a
                            href={`sms:${customer.Phone}`}
                            title="Message"
                            className="text-blue-500 hover:text-blue-700 flex items-center"
                          >
                            <MdMessage className="h-5 w-5" />
                          </a>
                          {(canUpdateCustomer || canDeleteCustomer) && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm" className="h-8 w-8 text-gray-600 hover:bg-gray-100">
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              {canUpdateCustomer && (
                              <DropdownMenuItem onClick={() => handleEdit(customer)} className="text-blue-600">
                                <Edit className="h-4 w-4 mr-2" />
                                Update
                              </DropdownMenuItem>
                              )}
                              {canDeleteCustomer && (
                              <DropdownMenuItem onClick={() => handleDelete(customer)} className="text-red-600">
                                <Trash2 className="h-4 w-4 mr-2" />
                                Delete
                              </DropdownMenuItem>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                    {/* Expandable Row for Transactions */}
                    {showTransactions && (
                      <TableRow className="bg-gray-50 transition-all duration-200">
                        <TableCell colSpan={21} className="p-0 border-t-0">
                          <div className="pl-12 py-4">
                            <div className="font-semibold text-lg mb-2 text-gray-900">
                              Recent Transactions for {truncateText(customer.FName)} {truncateText(customer.LName)}
                            </div>
                            {loadingTransactions[customer.Customercode] ? (
                              <div className="py-4 text-gray-500">Loading transactions...</div>
                            ) : transactionData[customer.Customercode]?.data?.length === 0 || !(((transactionData[customer.Customercode]?.data) || []).some((tx: any) => ((tx["Invoice Amt"] ?? 0) !== 0 || (tx.Payment ?? 0) !== 0 || (tx["Due Amount"] ?? 0) !== 0))) ? (
                              <div className="py-4 text-gray-500">No transactions found</div>
                            ) : (
                              <div className="overflow-x-auto">
                                <Table className="w-auto min-w-[600px]">
                                  <TableHeader>
                                    <TableRow>
                                      <TableHead className="whitespace-nowrap">Date</TableHead>
                                      <TableHead className="whitespace-nowrap">Tx Type</TableHead>
                                      <TableHead className="whitespace-nowrap">Invoice Amt</TableHead>
                                      <TableHead className="whitespace-nowrap">Payment</TableHead>
                                      <TableHead className="whitespace-nowrap">Due Amount</TableHead>
                                    </TableRow>
                                  </TableHeader>
                                  <TableBody>
                                    {transactionData[customer.Customercode]?.data?.map((tx, idx) => (
                                      <TableRow key={idx}>
                                        <TableCell className="whitespace-nowrap">{formatDate(tx.Date)}</TableCell>
                                        <TableCell className="whitespace-nowrap">{tx["Tx Type"]}</TableCell>
                                        <TableCell className="whitespace-nowrap">₹{tx["Invoice Amt"].toFixed(2)}</TableCell>
                                        <TableCell className="whitespace-nowrap">₹{tx.Payment.toFixed(2)}</TableCell>
                                        <TableCell className="whitespace-nowrap">₹{tx["Due Amount"].toFixed(2)}</TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              </div>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>
      {/* Card view for <=768px (md:hidden) */}
      <div className="block lg:hidden mt-72 px-4 pb-4">
        <div ref={mobileCardsContainerRef} className="mobile-cards-container space-y-4 max-h-[calc(100vh-140px)] overflow-y-auto">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="flex items-center gap-3 text-blue-600">
                <Loader2 className="h-6 w-6 animate-spin" />
                <span className="text-lg">Loading customers...</span>
              </div>
            </div>
          ) : allCustomers.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <div className="text-lg mb-2">No customers found</div>
              <div className="text-sm">Try adjusting your search criteria</div>
            </div>
          ) : (
            allCustomers.map((customer, index) => {
            const isExpanded = expandedRow === customer.Customercode;
            const showDetails = isExpanded && expandedMode === 'details';
            const showTransactions = isExpanded && expandedMode === 'transactions';
            const serialNumber = index + 1;
            
            return (
              <div key={customer.Customercode} className="bg-white rounded-lg shadow border">
                {/* Customer details section with relative positioning for the collapse button */}
                <div className="relative p-4">
                  <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2 flex-1 pr-2">
                        <span className="font-medium text-gray-700 text-sm min-w-[40px]">#{serialNumber}</span>
                        <div className="font-semibold text-lg break-words leading-tight">
                        {customer.FName} {customer.LName}
                        </div>
                      </div>
                      <div className="flex items-start gap-2 flex-shrink-0">
                        {(canUpdateCustomer || canDeleteCustomer) && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" className="h-8 w-8 text-gray-600 hover:bg-gray-100 flex-shrink-0">
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {canUpdateCustomer && (
                            <DropdownMenuItem onClick={() => handleEdit(customer)} className="text-blue-600">
                              <Edit className="h-4 w-4 mr-2" />
                              Update
                            </DropdownMenuItem>
                            )}
                            {canDeleteCustomer && (
                            <DropdownMenuItem onClick={() => handleDelete(customer)} className="text-red-600">
                              <Trash2 className="h-4 w-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                        )}
                      </div>
                    </div>
                    {/* Main customer details - now with label on left, value on right */}
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-500">Mobile:</span> 
                        <span>{customer.Phone}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">Email:</span> 
                        <span>{customer.Emailid || 'N/A'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">First Sale:</span>
                        <span>{formatDate(customer["First sale date"])}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">Last Sale:</span>
                        <span>{formatDate(customer["Last sale date"])}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">Days:</span>
                        <span>{customer["Number of days till today"] ?? 'N/A'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">Opening Balance:</span>
                        <span>{customer.OpeningBalance?.toLocaleString('en-IN', {
                          style: 'currency',
                          currency: 'INR',
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2
                        }) || '₹0.00'}</span>
                      </div>
                    </div>
                    <div className="mt-3 flex justify-end gap-2">
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => { setSelectedCustomer(customer); setShowWhatsAppModal(true); }}
                        className="text-green-600 hover:text-green-700 flex items-center"
                        title="WhatsApp"
                      >
                        <FaWhatsapp className="h-5 w-5" />
                      </Button>
                      <a
                        href={`sms:${customer.Phone}`}
                        title="Message"
                        className="text-blue-500 hover:text-blue-700 flex items-center"
                      >
                        <MdMessage className="h-5 w-5" />
                      </a>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleMoreClick(customer.Customercode)}
                        className={`text-blue-600 hover:text-blue-700 ${showDetails ? 'bg-blue-50' : ''}`}
                      >
                        {showDetails ? 'Show Less' : 'More'}
                      </Button>
                    </div>
                    {showDetails && (
                      <div className="mt-3 pt-3 border-t">
                        {/* Detailed customer info - now with label on left, value on right */}
                        <div className="space-y-1 text-sm">
                          <div className="flex justify-between">
                            <span className="font-medium text-gray-600">Invoiced Amount:</span>
                            <span>₹{(customer["Total Invoiced Amount"] ?? 0).toFixed(2)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="font-medium text-gray-600">Paid Amount:</span>
                            <span>₹{(customer["Total Paid Amount"] ?? 0).toFixed(2)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="font-medium text-gray-600">Due Amount:</span>
                            <span>₹{(customer["Total Due Amount"] ?? 0).toFixed(2)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="font-medium text-gray-600">Invoices:</span>
                            <span>{customer["Total Invoices Made"] ?? 0}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="font-medium text-gray-600">GSTN:</span>
                            <span>{customer.GSTN || 'N/A'}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="font-medium text-gray-600">Address:</span>
                            <span>{customer.Address || 'N/A'}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="font-medium text-gray-600">State:</span>
                            <span>{customer.State || 'N/A'}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="font-medium text-gray-600">Avg Value:</span>
                            <span>₹{(customer["Avg Invoice Value"] ?? 0).toFixed(2)}</span>
                          </div>
                        </div>
                      </div>
                    )}
                  
                  {/* Collapse button at bottom-left corner - mobile only; hide on Android when More (details) is open */}
                  {!(isAndroid && showDetails) && (
                    <div className="absolute bottom-2 left-2">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleRowClick(customer.Customercode)}
                        className={`transition-all duration-200 rounded-full hover:bg-gray-100 focus:outline-none ${showTransactions ? 'bg-gray-100' : ''}`}
                        aria-label={showTransactions ? 'Hide transactions' : 'Show transactions'}
                      >
                        <ChevronRight
                          className={`h-5 w-5 transition-transform duration-200 ease-in-out ${showTransactions ? 'rotate-90' : ''}`}
                        />
                      </Button>
                    </div>
                  )}
                </div>
                {/* Transactions section - outside the customer details container */}
                {showTransactions && (
                  <div className="px-4 pb-4">
                    <div className="font-semibold text-base mb-2 text-gray-900">
                      Recent Transactions for {truncateText(customer.FName)} {truncateText(customer.LName)}
                    </div>
                    {loadingTransactions[customer.Customercode] ? (
                      <div className="py-4 text-gray-500">Loading transactions...</div>
                    ) : transactionData[customer.Customercode]?.data?.length === 0 || !(((transactionData[customer.Customercode]?.data) || []).some((tx: any) => ((tx["Invoice Amt"] ?? 0) !== 0 || (tx.Payment ?? 0) !== 0 || (tx["Due Amount"] ?? 0) !== 0))) ? (
                      <div className="py-4 text-gray-500">No transactions found</div>
                    ) : (
                      <div className="space-y-3">
                        {transactionData[customer.Customercode]?.data?.map((tx, idx) => (
                          <div key={idx} className="rounded-lg border bg-gray-50 p-3 flex flex-col gap-1">
                            <div className="flex justify-between"><span className="font-medium text-gray-600">Date:</span><span>{formatDate(tx.Date)}</span></div>
                            <div className="flex justify-between"><span className="font-medium text-gray-600">Tx Type:</span><span>{tx["Tx Type"]}</span></div>
                            <div className="flex justify-between"><span className="font-medium text-gray-600">Invoice Amt:</span><span>₹{tx["Invoice Amt"].toFixed(2)}</span></div>
                            <div className="flex justify-between"><span className="font-medium text-gray-600">Payment:</span><span>₹{tx.Payment.toFixed(2)}</span></div>
                            <div className="flex justify-between"><span className="font-medium text-gray-600">Due Amount:</span><span>₹{tx["Due Amount"].toFixed(2)}</span></div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })
        )}
        {isLoadingMore && (
          <div className="flex justify-center py-4">
            <div className="flex items-center gap-2 text-blue-600">
              <Loader2 className="h-5 w-5 animate-spin" />
              <span className="text-sm">Loading more customers...</span>
            </div>
          </div>
        )}
        {!isLoadingMore && hasMoreData && (
          <div className="text-center py-4 text-gray-500 text-sm">
            Scroll down to load more customers
          </div>
        )}
        {!hasMoreData && allCustomers.length > 0 && (
          <div className="text-center py-4 text-gray-500 text-sm">
            All {allCustomers.length} customers loaded
          </div>
        )}
        </div>
        
        {/* Scroll to top button for mobile */}
        {isMobile && showScrollToTop && (
          <button
            onClick={() => {
              const scrollableContainer = document.querySelector('.mobile-cards-container');
              if (scrollableContainer) {
                scrollableContainer.scrollTo({ top: 0, behavior: 'smooth' });
              }
            }}
            className="fixed bottom-6 right-6 z-50 bg-blue-600 hover:bg-blue-700 text-white rounded-full p-3 shadow-lg transition-all duration-200"
            aria-label="Scroll to top"
          >
            <ChevronRight className="h-5 w-5 transform rotate-[-90deg]" />
          </button>
        )}
      </div>

      <div className="hidden lg:flex items-center justify-between">
        <div className="text-sm text-gray-500">
          Showing {startIndex} to {endIndex} of {totalRecords} records
        </div>
        {pageSize !== 'all' && (
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
            disabled={currentPage === 1}
          >
            Previous
          </Button>
          <span className="text-sm">
            Page {currentPage} of {totalPages}
          </span>
          <Button
            variant="outline"
            onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
            disabled={currentPage === totalPages}
          >
            Next
          </Button>
        </div>
        )}
      </div>

      {/* Add Customer Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="max-w-lg w-full max-h-[90vh] sm:max-h-[80vh] overflow-y-auto p-2 sm:p-6">
          <DialogHeader>
            <DialogTitle>Add Customer</DialogTitle>
            <DialogDescription>
              Fill the form to add a new customer.
            </DialogDescription>
          </DialogHeader>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onAddCustomer)} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="MobileNo"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Mobile</FormLabel>
                      <FormControl>
                        <Input 
                          placeholder="Enter mobile number" 
                          value={field.value} 
                          onChange={(e) => {
                            // Only allow numbers and limit to 10 digits
                            const value = e.target.value.replace(/\D/g, '').slice(0, 10);
                            field.onChange(value);
                          }}
                          type="tel"
                          maxLength={10}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="ContactName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Name</FormLabel>
                      <FormControl>
                        <Input placeholder="Enter name" value={field.value} onChange={field.onChange} />
                      </FormControl>
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="CompanyName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Company</FormLabel>
                      <FormControl>
                        <Input placeholder="Enter company name" value={field.value} onChange={field.onChange} />
                      </FormControl>
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="EmailID"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email</FormLabel>
                      <FormControl>
                        <Input placeholder="Enter email" value={field.value} onChange={field.onChange} />
                      </FormControl>
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="GSTNo"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>GSTN</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="Enter GST number"
                          value={field.value ?? ''}
                          onChange={e => {
                            const { value } = sanitizeGstInput(e.target.value);
                            field.onChange(value);
                          }}
                          maxLength={GST_MAX_LENGTH}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="Shipping_Address"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Address</FormLabel>
                      <FormControl>
                        <Input placeholder="Enter address" value={field.value} onChange={field.onChange} />
                      </FormControl>
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="State"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>State</FormLabel>
                      <FormControl>
                        <select
                          className="border rounded px-2 py-1 w-full"
                          value={field.value}
                          onChange={field.onChange}
                        >
                          <option value="">{isStatesLoading ? 'Loading...' : 'Select'}</option>
                          {statesError && <option value="">Error loading states</option>}
                          {states && states.map((state: any) => (
                            <option key={state.Code} value={state.State}>{state.State}</option>
                          ))}
                        </select>
                      </FormControl>
                    </FormItem>
                  )}
                />
                {/* Opening Balance Field */}
                <FormField
                  control={form.control}
                  name="OpeningBalance"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Opening Balance</FormLabel>
                      <FormControl>
                        <Input type="number" placeholder="Enter opening balance" {...field} onChange={e => field.onChange(parseFloat(e.target.value))}/>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                {/* Group Dropdown */}
                <FormField
                  control={form.control}
                  name="CGroupID"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Group</FormLabel>
                      <div className="flex items-center gap-2">
                        <select
                          className="border rounded px-2 py-1 w-full"
                          value={field.value}
                          onChange={field.onChange}
                        >
                          <option value="">Select group</option>
                          {groups.map((group) => (
                            <option key={group.id} value={group.id}>
                              {group.Name}
                            </option>
                          ))}
                        </select>
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <DialogFooter>
                <Button type="submit">Add Customer</Button>
                <Button type="button" variant="outline" onClick={() => form.reset()}>Clear Field</Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      {/* Update Customer Modal */}
      <Dialog open={showUpdateModal} onOpenChange={setShowUpdateModal}>
        <DialogContent className="max-w-lg w-full max-h-[90vh] sm:max-h-[80vh] overflow-y-auto p-2 sm:p-6">
          <DialogHeader>
            <DialogTitle>Update Customer</DialogTitle>
            <DialogDescription>
              Fill the form to update the customer.
            </DialogDescription>
          </DialogHeader>
          <Form {...updateForm}>
            <form onSubmit={updateForm.handleSubmit(onUpdateCustomer)} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={updateForm.control}
                  name="MobileNo"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Mobile</FormLabel>
                      <FormControl>
                        <Input 
                          placeholder="Enter mobile number" 
                          value={field.value} 
                          onChange={(e) => {
                            // Only allow numbers and limit to 10 digits
                            const value = e.target.value.replace(/\D/g, '').slice(0, 10);
                            field.onChange(value);
                          }}
                          type="tel"
                          maxLength={10}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={updateForm.control}
                  name="ContactName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Name</FormLabel>
                      <FormControl>
                        <Input placeholder="Enter name" value={field.value} onChange={field.onChange} />
                      </FormControl>
                    </FormItem>
                  )}
                />
                <FormField
                  control={updateForm.control}
                  name="CompanyName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Company</FormLabel>
                      <FormControl>
                        <Input placeholder="Enter company name" value={field.value} onChange={field.onChange} />
                      </FormControl>
                    </FormItem>
                  )}
                />
                <FormField
                  control={updateForm.control}
                  name="EmailID"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email</FormLabel>
                      <FormControl>
                        <Input placeholder="Enter email" value={field.value} onChange={field.onChange} />
                      </FormControl>
                    </FormItem>
                  )}
                />
                <FormField
                  control={updateForm.control}
                  name="GSTNo"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>GSTN</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="Enter GST number"
                          value={field.value ?? ''}
                          onChange={e => {
                            const { value } = sanitizeGstInput(e.target.value);
                            field.onChange(value);
                          }}
                          maxLength={GST_MAX_LENGTH}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={updateForm.control}
                  name="Shipping_Address"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Address</FormLabel>
                      <FormControl>
                        <Input placeholder="Enter address" value={field.value} onChange={field.onChange} />
                      </FormControl>
                    </FormItem>
                  )}
                />
                <FormField
                  control={updateForm.control}
                  name="State"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>State</FormLabel>
                      <FormControl>
                        <select
                          className="border rounded px-2 py-1 w-full"
                          value={field.value}
                          onChange={field.onChange}
                        >
                          <option value="">{isStatesLoading ? 'Loading...' : 'Select'}</option>
                          {statesError && <option value="">Error loading states</option>}
                          {states && states.map((state: any) => (
                            <option key={state.Code} value={state.State}>{state.State}</option>
                          ))}
                        </select>
                      </FormControl>
                    </FormItem>
                  )}
                />
                {/* Group Dropdown */}
                <FormField
                  control={updateForm.control}
                  name="CGroupID"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Group</FormLabel>
                      <div className="flex items-center gap-2">
                        <select
                          className="border rounded px-2 py-1 w-full"
                          value={field.value}
                          onChange={field.onChange}
                        >
                          <option value="">Select group</option>
                          {groups.map((group) => (
                            <option key={group.id} value={group.id}>
                              {group.Name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </FormItem>
                  )}
                />
              </div>
              <DialogFooter>
                <Button type="submit">Update Customer</Button>
                <DialogClose asChild>
                  <Button type="button" variant="outline">
                    Cancel
                  </Button>
                </DialogClose>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Confirm Delete</DialogTitle>
            <DialogDescription>
              Do you really want to delete this customer?
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end space-x-2">
            <Button
              variant="outline"
              onClick={() => setShowDeleteDialog(false)}
            >
              No
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteCustomer}
            >
              Yes
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Group Management Modal */}
      <Dialog open={showGroupModal} onOpenChange={setShowGroupModal}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>{editingGroup ? 'Edit Group' : 'Add New Group'}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="name" className="text-right">
                Name
              </Label>
              <Input
                id="name"
                value={newGroupName}
                onChange={(e) => setNewGroupName(e.target.value)}
                className="col-span-3"
              />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={handleSaveEditGroup}>
              {editingGroup ? 'Update' : '+ Add'}
            </Button>
          </DialogFooter>

          {/* Group List */}
          <div className="mt-6">
            <h3 className="text-lg font-semibold mb-4">Existing Groups</h3>
            <div className="space-y-2 max-h-[300px] overflow-y-auto">
              {groups.map((group) => (
                <div key={group.id} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                  <span className="font-medium">{group.Name}</span>
                  <div className="flex gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleEditGroup(group)}
                      className="text-blue-600 hover:bg-blue-100"
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteGroup(group)}
                      className="text-red-600 hover:bg-red-100"
                    >
                      <Trash className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Group Delete Confirmation Dialog */}
      <Dialog open={showGroupDeleteDialog} onOpenChange={setShowGroupDeleteDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Confirm Delete</DialogTitle>
            <DialogDescription>
              Do you really want to delete this group?
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end space-x-2">
            <Button
              variant="outline"
              onClick={() => setShowGroupDeleteDialog(false)}
            >
              No
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirmDeleteGroup}
            >
              Yes
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {selectedCustomer && (
        <WhatsAppPopup
          open={showWhatsAppModal}
          onClose={() => setShowWhatsAppModal(false)}
          customer={selectedCustomer}
        />
      )}

      {/* Export PDF Dialog */}
      <UIDialog open={showExportDialog} onOpenChange={setShowExportDialog}>
        <UIDialogContent>
          <UIDialogHeader>
            <UIDialogTitle>Select PDF Export Type</UIDialogTitle>
          </UIDialogHeader>
          <div className="flex flex-col gap-3 mt-2">
            <Button
              onClick={() => {
                setShowExportDialog(false);
                exportCustomersToPDF(customers);
              }}
              className="w-full font-semibold bg-green-600 hover:bg-green-700 py-3"
            >
              Download Customer
            </Button>
            <Button
              onClick={() => {
                setShowExportDialog(false);
                exportTransactionsToPDF(customers, transactionData);
              }}
              className="w-full font-semibold bg-green-600 hover:bg-green-700 py-3"
            >
              Download Transactions
            </Button>
            <UIDialogClose asChild>
              <Button variant="outline" className="w-full">Cancel</Button>
            </UIDialogClose>
          </div>
        </UIDialogContent>
      </UIDialog>
    </div>
  );
};

export default CustomerPage;
